import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { SyncIcon, TableIcon } from '@primer/octicons-react';
import { LTText, LTTooltip } from '@lambdatestincprivate/lt-components';
import { browserDisplayName, formatDurationMs, osDisplayName, osLabel, toBrowserKey, toOSKey, truncateMiddle } from '../format';
import { BrowserIcon } from '../icons/BrowserIcon';
import { ClockIcon } from '../icons/ClockIcon';
import { OSIcon } from '../icons/OSIcon';
import { StatusIcon } from '../icons/StatusIcon';
import { readDatasetParams } from '../../data/pack-store';
import { datasetValues } from '../../model/detail';
import { DatasetValuesModal } from '../Detail/Header/DatasetValuesModal';
import { testCaseHref } from '../links';
import styles from './instances.module.css';
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
function MetaItem(_ref) {
  var icon = _ref.icon,
    label = _ref.label,
    srPrefix = _ref.srPrefix,
    tooltip = _ref.tooltip;
  var content = /*#__PURE__*/_jsxs("span", {
    className: styles.metaItem,
    children: [srPrefix && /*#__PURE__*/_jsxs("span", {
      className: "ev-sr-only",
      children: [srPrefix, ": "]
    }), icon, /*#__PURE__*/_jsx(LTText, {
      text: label,
      variant: LTText.TextVariant.SMALL_REGULAR,
      color: "var(--lt-text-secondary)"
    })]
  });
  if (!tooltip) return content;
  return /*#__PURE__*/_jsx(LTTooltip, {
    text: tooltip,
    direction: "bottom",
    customTriggerStyle: {
      display: 'inline-flex'
    },
    children: content
  });
}
var Separator = function Separator() {
  return /*#__PURE__*/_jsx("span", {
    className: styles.metaDot,
    "aria-hidden": "true"
  });
};

/** Dataset meta item: opens the Dataset Values modal, reading the row's
 * values (execution.json test_params) only when asked. */
function DatasetMeta(_ref2) {
  var _dataset$name;
  var testId = _ref2.testId,
    dataset = _ref2.dataset;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    open = _React$useState2[0],
    setOpen = _React$useState2[1];
  // dataset.json carries the row's values in newer packs; older ones need
  // execution.json test_params, read on first open.
  var _React$useState3 = React.useState(function () {
      return dataset.values ? datasetValues(dataset, []) : null;
    }),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    values = _React$useState4[0],
    setValues = _React$useState4[1];
  var openModal = function openModal() {
    setOpen(true);
    if (values === null) {
      void readDatasetParams(testId).then(function (params) {
        return setValues(datasetValues(dataset, params));
      });
    }
  };
  var name = (_dataset$name = dataset.name) !== null && _dataset$name !== void 0 ? _dataset$name : 'Dataset';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(LTTooltip, {
      text: dataset.rowIndex !== null ? "View dataset values \xB7 row ".concat(dataset.rowIndex) : 'View dataset values',
      direction: "bottom",
      customTriggerStyle: {
        display: 'inline-flex'
      },
      children: /*#__PURE__*/_jsxs("button", {
        type: "button",
        className: styles.metaAction,
        onClick: openModal,
        "aria-haspopup": "dialog",
        "aria-label": "Dataset ".concat(name, ": view values"),
        "data-testid": "evidence-row-dataset",
        children: [/*#__PURE__*/_jsx(TableIcon, {
          size: 14,
          "aria-hidden": "true"
        }), /*#__PURE__*/_jsx("span", {
          className: styles.metaActionText,
          children: name
        })]
      })
    }), open && /*#__PURE__*/_jsx(DatasetValuesModal, {
      dataset: dataset,
      values: values,
      onClose: function onClose() {
        return setOpen(false);
      }
    })]
  });
}

/**
 * One test-instance row: status icon, title, then the dot-separated meta
 * line (testcase id, OS, browser, duration) — the Test Manager row, fed
 * from result.yaml instead of an API. The title opens the detail view;
 * the TC id and dataset are their own controls; pending/error rows stay
 * inert.
 */
export var InstanceRow = /*#__PURE__*/React.memo(function InstanceRow(_ref3) {
  var _row$env, _row$env2, _row$env3, _row$env4, _row$attempts$length, _row$attempts;
  var row = _ref3.row,
    onSelect = _ref3.onSelect;
  if (row.state === 'pending') {
    return /*#__PURE__*/_jsxs("div", {
      className: styles.rowInner,
      children: [/*#__PURE__*/_jsx("span", {
        className: "ev-sr-only",
        children: "Loading test details"
      }), /*#__PURE__*/_jsx("span", {
        className: styles.skeletonIcon,
        "aria-hidden": "true"
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.rowBody,
        "aria-hidden": "true",
        children: [/*#__PURE__*/_jsx("span", {
          className: "".concat(styles.skeletonBar, " ").concat(styles.skeletonTitle)
        }), /*#__PURE__*/_jsx("span", {
          className: "".concat(styles.skeletonBar, " ").concat(styles.skeletonMeta)
        })]
      })]
    });
  }
  if (row.state === 'error') {
    return /*#__PURE__*/_jsxs("div", {
      className: styles.rowInner,
      children: [/*#__PURE__*/_jsx(StatusIcon, {
        status: "unknown"
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.rowBody,
        children: [/*#__PURE__*/_jsx(LTText, {
          text: row.id,
          variant: LTText.TextVariant.SUBHEADER_BOLD,
          color: "var(--lt-text-primary)",
          className: styles.rowTitle
        }), /*#__PURE__*/_jsx(LTText, {
          text: "Couldn't read this test's result".concat(row.errorMessage ? " \u2014 ".concat(row.errorMessage) : ''),
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "var(--lt-text-secondary)"
        })]
      })]
    });
  }
  var meta = [];
  if (row.testcaseId) {
    var _row$tcId, _row$tcId2;
    var tcLabel = (_row$tcId = row.tcId) !== null && _row$tcId !== void 0 ? _row$tcId : truncateMiddle(row.testcaseId, 15);
    var tcHref = testCaseHref(row.projectId, row.testcaseId);
    meta.push(tcHref ? /*#__PURE__*/_jsx(LTTooltip, {
      text: "Open test case in Test Manager",
      direction: "bottom",
      customTriggerStyle: {
        display: 'inline-flex'
      },
      children: /*#__PURE__*/_jsx("a", {
        className: styles.metaAction,
        href: tcHref,
        target: "_blank",
        rel: "noopener noreferrer",
        "aria-label": "Test case ".concat(tcLabel, " (opens in a new tab)"),
        "data-testid": "evidence-row-tc",
        children: /*#__PURE__*/_jsx("span", {
          className: styles.metaActionText,
          children: tcLabel
        })
      })
    }, "tc") : /*#__PURE__*/_jsx(MetaItem, {
      label: tcLabel,
      tooltip: (_row$tcId2 = row.tcId) !== null && _row$tcId2 !== void 0 ? _row$tcId2 : row.testcaseId,
      srPrefix: "Test case"
    }, "tc"));
  }
  // Data-driven runs repeat one test case per dataset row (kaneai listing
  // shows the dataset name). Display-only: a control can't nest in the row
  // button — the detail header opens the values.
  if (row.dataset) {
    meta.push(/*#__PURE__*/_jsx(DatasetMeta, {
      testId: row.id,
      dataset: row.dataset
    }, "dataset"));
  }
  var os = toOSKey((_row$env = row.env) === null || _row$env === void 0 ? void 0 : _row$env.os);
  var osText = osLabel((_row$env2 = row.env) === null || _row$env2 === void 0 ? void 0 : _row$env2.os, (_row$env3 = row.env) === null || _row$env3 === void 0 ? void 0 : _row$env3.osVersion);
  if (osText) {
    // The icon is aria-hidden, so the OS/browser NAME must reach screen
    // readers as text — a bare "14.5" is meaningless without "macOS".
    meta.push(/*#__PURE__*/_jsx(MetaItem, {
      icon: /*#__PURE__*/_jsx(OSIcon, {
        os: os,
        size: 16
      }),
      label: osText,
      srPrefix: osDisplayName(os)
    }, "os"));
  }
  if ((_row$env4 = row.env) !== null && _row$env4 !== void 0 && _row$env4.browser) {
    var _row$env$browserVersi;
    var browser = toBrowserKey(row.env.browser);
    meta.push(/*#__PURE__*/_jsx(MetaItem, {
      icon: /*#__PURE__*/_jsx(BrowserIcon, {
        browser: browser,
        size: 16
      }),
      label: (_row$env$browserVersi = row.env.browserVersion) !== null && _row$env$browserVersi !== void 0 ? _row$env$browserVersi : row.env.browser,
      srPrefix: browserDisplayName(browser)
    }, "browser"));
  }
  if (row.durationMs !== undefined) {
    meta.push(/*#__PURE__*/_jsx(MetaItem, {
      icon: /*#__PURE__*/_jsx(ClockIcon, {
        size: 16
      }),
      label: formatDurationMs(row.durationMs),
      srPrefix: "Duration"
    }, "duration"));
  }
  // result.yaml's authored chronological attempts list — more than one
  // entry means the test was retried; earlier attempts are viewable from
  // the detail header's switcher.
  var retries = ((_row$attempts$length = (_row$attempts = row.attempts) === null || _row$attempts === void 0 ? void 0 : _row$attempts.length) !== null && _row$attempts$length !== void 0 ? _row$attempts$length : 0) - 1;
  if (retries > 0) {
    meta.push(/*#__PURE__*/_jsx(MetaItem, {
      icon: /*#__PURE__*/_jsx(SyncIcon, {
        size: 14,
        "aria-hidden": "true"
      }),
      label: "".concat(retries, " ").concat(retries === 1 ? 'retry' : 'retries'),
      srPrefix: "Retried"
    }, "retries"));
  }

  // Only the title navigates (user decision): the meta line carries its
  // own controls (TC link, dataset), so the row itself isn't a button.
  return /*#__PURE__*/_jsxs("div", {
    className: styles.rowInner,
    children: [/*#__PURE__*/_jsx(StatusIcon, {
      status: row.status
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.rowBody,
      children: [onSelect ? /*#__PURE__*/_jsx("button", {
        type: "button"
        // The focus-restore target when the detail view closes.
        ,
        id: "ev-instance-row-".concat(row.id),
        className: styles.rowTitleButton,
        onClick: function onClick() {
          return onSelect(row.id);
        },
        title: row.title,
        children: row.title
      }) : /*#__PURE__*/_jsx(LTText, {
        text: row.title,
        variant: LTText.TextVariant.SUBHEADER_BOLD,
        color: "var(--lt-text-primary)",
        className: styles.rowTitle,
        title: row.title
      }), /*#__PURE__*/_jsx("span", {
        className: styles.metaLine,
        children: meta.map(function (item, i) {
          return /*#__PURE__*/_jsxs(React.Fragment, {
            children: [i > 0 && /*#__PURE__*/_jsx(Separator, {}), item]
          }, i);
        })
      })]
    })]
  });
});